import { useState } from 'react';
import { ChevronDown, ChevronRight, ArrowRight, Calendar, GitCompare } from 'lucide-react';
import { ReconciliationData, LeadCohortMetrics, JobScheduleMetrics } from '@/types/reports';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';

interface ReconciliationPanelProps {
  leadMetrics: LeadCohortMetrics;
  jobMetrics: JobScheduleMetrics;
  reconciliation: ReconciliationData;
}

export const ReconciliationPanel = ({ leadMetrics, jobMetrics, reconciliation }: ReconciliationPanelProps) => {
  const [leadsExpanded, setLeadsExpanded] = useState(false);
  const [jobsExpanded, setJobsExpanded] = useState(false);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      return format(parseISO(dateStr), 'MMM d');
    } catch {
      return dateStr;
    }
  };

  const leadsWithJobsInPeriod = leadMetrics.convertedLeads - reconciliation.leadsWithJobsOutsidePeriod;

  return (
    <Card className="overflow-hidden border-0 shadow-md bg-gradient-to-br from-background to-muted/20">
      <CardHeader className="py-3 px-4 bg-muted/30 border-b">
        <CardTitle className="text-sm flex items-center gap-2">
          <GitCompare className="h-4 w-4 text-primary" />
          How the Numbers Connect
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {/* Horizontal flow */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Starting: Converted Leads */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20">
            <span className="font-bold text-lg text-primary">{leadMetrics.convertedLeads}</span>
            <span className="text-xs text-muted-foreground">converted</span>
          </div>

          {/* Subtract leads with jobs outside */}
          {reconciliation.leadsWithJobsOutsidePeriod > 0 && (
            <>
              <span className="text-muted-foreground font-mono">−</span>
              <div 
                className={cn(
                  "group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all",
                  "bg-warning/10 border border-warning/20 hover:bg-warning/15"
                )}
                onClick={() => setLeadsExpanded(!leadsExpanded)}
              >
                <span className="font-bold text-lg text-warning">{reconciliation.leadsWithJobsOutsidePeriod}</span>
                <span className="text-xs text-warning/80">jobs on other days</span>
                {leadsExpanded ? (
                  <ChevronDown className="h-3 w-3 text-warning" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-warning" />
                )}
              </div>
            </>
          )}

          {/* Add jobs from leads outside */}
          {reconciliation.jobsFromLeadsOutsidePeriod > 0 && (
            <>
              <span className="text-muted-foreground font-mono">+</span>
              <div 
                className={cn(
                  "group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all",
                  "bg-success/10 border border-success/20 hover:bg-success/15"
                )}
                onClick={() => setJobsExpanded(!jobsExpanded)}
              >
                <span className="font-bold text-lg text-success">{reconciliation.jobsFromLeadsOutsidePeriod}</span>
                <span className="text-xs text-success/80">from earlier leads</span>
                {jobsExpanded ? (
                  <ChevronDown className="h-3 w-3 text-success" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-success" />
                )}
              </div>
            </>
          )}

          {/* Equals */}
          <span className="text-muted-foreground font-mono">=</span>

          {/* Result: Jobs Scheduled */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground shadow-sm">
            <span className="font-bold text-lg">{jobMetrics.totalJobs}</span>
            <span className="text-xs opacity-90">jobs</span>
          </div>
        </div>

        {/* Expanded details */}
        {leadsExpanded && reconciliation.leadsWithJobsOutsidePeriod > 0 && (
          <div className="mt-3 p-3 rounded-lg bg-warning/10 border border-warning/20 animate-fade-in">
            <div className="text-xs font-medium text-warning mb-2">
              Leads with jobs scheduled outside period:
            </div>
            <div className="flex flex-wrap gap-2">
              {reconciliation.leadsWithJobsOutsidePeriodList.map((item) => (
                <div 
                  key={item.leadId} 
                  className="flex items-center gap-1.5 px-2 py-1 rounded bg-card text-xs"
                >
                  <span className="font-mono text-primary">{item.leadNumber}</span>
                  <ArrowRight className="h-2.5 w-2.5 text-muted-foreground" />
                  <Calendar className="h-2.5 w-2.5 text-muted-foreground" />
                  <span className="text-muted-foreground">{formatDate(item.jobScheduledDate)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {jobsExpanded && reconciliation.jobsFromLeadsOutsidePeriod > 0 && (
          <div className="mt-3 p-3 rounded-lg bg-success/10 border border-success/20 animate-fade-in">
            <div className="text-xs font-medium text-success mb-2">
              Jobs from leads created outside period:
            </div>
            <div className="flex flex-wrap gap-2">
              {reconciliation.jobsFromLeadsOutsidePeriodList.map((item) => (
                <div 
                  key={item.jobId} 
                  className="flex items-center gap-1.5 px-2 py-1 rounded bg-card text-xs"
                >
                  <span className="font-mono text-primary">{item.jobNumber}</span>
                  <ArrowRight className="h-2.5 w-2.5 rotate-180 text-muted-foreground" />
                  <Calendar className="h-2.5 w-2.5 text-muted-foreground" />
                  <span className="text-muted-foreground">{formatDate(item.leadCreatedDate)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Perfect match */}
        {reconciliation.leadsWithJobsOutsidePeriod === 0 && reconciliation.jobsFromLeadsOutsidePeriod === 0 && (
          <div className="mt-2 text-xs text-success">
            ✓ All converted leads have jobs in this period
          </div>
        )}
      </CardContent>
    </Card>
  );
};
